import React, { FC, useState } from "react";
import { Box } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { font, subtitlesStyles } from "../../configs";
import { Spinner } from "../Spinner";

import { findBrandByDomain } from "../../configs/configLoader";
import { activeStylesContinue } from "../../configs";
import { hoverStylesContinue, titleStyles } from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import { capitalizeFirstLetter } from "../../utils";

import "./index.css";

interface IPropsStepScanResult {
  triggerStarted: boolean;
  photo: string | null;
  error: boolean;
  nextStep: () => void;
  restart: () => void;
}

const StepScanResult: FC<IPropsStepScanResult> = ({
  triggerStarted,
  photo,
  error,
  nextStep,
  restart,
}) => {
  const { t } = useTranslation("components/device_select");

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [hovered, setHovered] = useState(false);

  const brandDefined = findBrandByDomain();

  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isCustomBrands = isIntersport || isBlackstore;

  const mergedButtonStyles: React.CSSProperties = {
    ...(isCustomBrands ? activeStylesContinue : {}),
    ...(isIntersport && hovered ? hoverStylesContinue : {}),
    textTransform:
      titleStyles.textTransform as React.CSSProperties["textTransform"],
    marginBottom: "10px",
    position: "relative",
    padding: "16px",
    height: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered ? 1 : 0,
    transform: hovered ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  const descriptionTextTransform =
    subtitlesStyles.textTransform === "capitalize"
      ? "none"
      : (subtitlesStyles.textTransform as React.CSSProperties["textTransform"]);

  const descriptionStyles = {
    color: error ? "red" : "#2E2E2E",
    fontSize: "18px",
    fontWeight: 600,
    textAlign: subtitlesStyles.textAlign as React.CSSProperties["textAlign"],
    textTransform: isIntersport ? "uppercase" : descriptionTextTransform,
    justifyContent:
      subtitlesStyles.textAlign as React.CSSProperties["justifyContent"],
    fontFamily: isMobile
      ? `${font}, sans-serif`
      : `${font}, sans-serif !important`,
    lineHeight: "22px",
    marginTop: isMobile ? "20px" : "30px",
    marginBottom: "40px",
    margin: 0,
  };

  const descriptionText = error ? t("result.error") : t("result.text");

  return (
    <>
      <Box
        className="main-container"
        style={{
          margin: 0,
        }}
      >
        <Box
          style={{
            padding: "30px",
          }}
        >
          <img
            src={photo || ""}
            alt="screen_photo"
            style={{
              width: "100%",
              maxHeight: "47vh",
              height: "auto",
              borderRadius: "10px",
              objectFit: "contain",
              display: "block",
              margin: "0 auto",
            }}
          />
        </Box>

        {!triggerStarted ? (
          <>
            <span
              style={{
                ...descriptionStyles,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {subtitlesStyles.textTransform === "capitalize"
                ? capitalizeFirstLetter(descriptionText)
                : descriptionText}
            </span>
            <button
              type="button"
              className="continue-button"
              onClick={() => {
                nextStep();
              }}
              style={mergedButtonStyles}
              onMouseEnter={() => {
                setHovered(true);
              }}
              onMouseLeave={() => {
                setHovered(false);
              }}
            >
              <div
                style={{ display: "inline-flex", alignItems: "center", gap: 0 }}
              >
                {t("result.continue")}

                {isIntersport && (
                  <span style={arrowContainerStyle} aria-hidden>
                    <ChevronRight style={iconStyle} />
                  </span>
                )}
              </div>
            </button>
          </>
        ) : (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner />
          </div>
        )}
        <button
          type="button"
          className="continue-button-skip"
          onClick={restart}
          style={{
            textTransform:
              descriptionStyles.textTransform as React.CSSProperties["textTransform"],
            marginBottom: "10px",
            borderRadius: mergedButtonStyles?.borderRadius,
          }}
        >
          {t("result.skip")}
        </button>
      </Box>
    </>
  );
};

export default StepScanResult;
